/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  ButtonBase,
  IconButton,
  Paper,
  Typography,
  Fade
} from '@material-ui/core'
import LeftArrowIcon from '@material-ui/icons/ChevronLeft'
import RightArrowIcon from '@material-ui/icons/ChevronRight'
import DoubleArrowIcon from '@material-ui/icons/DoubleArrow'
import moment from 'moment-jalaali'
import momentJalaali from 'moment-jalaali'
import { useEffect, useMemo, useRef, useState } from 'react'

import { DateRangePicker } from '../../../components/DatePicker/DateRangePicker'
import {
  formatDateFullObject,
  getDatesList,
  addDaysToDate,
  getTodayDate,
  DateObject,
  isWeekend
} from '../../../helpers/date'
import { lang } from '../../../localization'
import useStyles from './styles/InventoryDateRow.styles'

interface DateRowProps {
  selectedDateInDatePicker: DateObject
  setselectedDateInDatePicker: React.Dispatch<React.SetStateAction<DateObject>>
  range?: 'THREE_MONTH' | 'MONTH' | 'YEAR'
}

const DateRow: React.FC<DateRowProps> = ({
  selectedDateInDatePicker,
  setselectedDateInDatePicker,
  range = 'YEAR'
}) => {
  const classes = useStyles()

  const perPage = useMemo(() => {
    switch (range) {
      case 'YEAR':
        return 12
      case 'THREE_MONTH':
        return 9 // 3 months * 3 10-day segments
      case 'MONTH':
      default:
        return 31
    }
  }, [range])

  const [isOpen, setisOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    /**
     * Alert if clicked on outside of element
     */
    function handleClickOutside(event: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef?.current?.contains(event.target as Node) &&
        isOpen
      ) {
        setisOpen(false)
      }
    }

    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [wrapperRef, isOpen])

  // Date list generation based on view
  const datesList = useMemo(() => {
    switch (range) {
      case 'YEAR':
        return getDatesList(
          12,
          selectedDateInDatePicker.gregorianStartDate,
          'month'
        ) // 12 months
      case 'THREE_MONTH':
        return getDatesList(
          9,
          selectedDateInDatePicker.gregorianStartDate,
          '10-days'
        ) // 3x10-day periods
      case 'MONTH':
      default:
        return getDatesList(
          31,
          selectedDateInDatePicker.gregorianStartDate,
          'day'
        ) // 31 days for month view
    }
  }, [range, selectedDateInDatePicker.gregorianStartDate])

  // Inside the DateRow component
  const displayDate = useMemo(() => {
    if (range === 'YEAR') {
      // For yearly range, display either the year range or a single year if they're the same
      const startYear = moment(datesList[0].gregorianDate.date).format('YYYY')
      const endYear = moment(
        datesList[datesList.length - 1].gregorianDate.date
      ).format('YYYY')

      // Show a single year if start and end years are the same
      return startYear === endYear ? startYear : `${startYear} - ${endYear}`
    } else if (range === 'THREE_MONTH') {
      // For three-month range, display either the year range or a single year if they're the same
      const startDate = moment(datesList[0].gregorianDate.date)
      const endDate = moment(datesList[datesList.length - 1].gregorianDate.date)
      const startYear = startDate.format('YYYY')
      const endYear = endDate.format('YYYY')

      // Show a single year if start and end years are the same
      return startYear === endYear ? startYear : `${startYear} - ${endYear}`
    } else {
      // For monthly range, use the original format
      return formatDateFullObject(datesList[0])
    }
  }, [range, datesList])

  const handleRangeChange = (offset: number, isSingleStep: boolean) => {
    let dateOffset: number

    if (range === 'YEAR') {
      dateOffset = isSingleStep ? 31 : 365 // 1 month or 1 year (12 months)
    } else if (range === 'THREE_MONTH') {
      dateOffset = isSingleStep ? 31 : 90 // 10 days or 3 months (90 days)
    } else {
      dateOffset = isSingleStep ? 1 : 31 // 1 day or 1 month (approx 30 days)
    }

    const newStartDate =
      range !== 'MONTH'
        ? momentJalaali(
            addDaysToDate(
              selectedDateInDatePicker.gregorianStartDate!,
              offset * dateOffset
            )
          )
            .startOf('month')
            .format('YYYY-MM-DD')
        : momentJalaali(
            addDaysToDate(
              selectedDateInDatePicker.gregorianStartDate!,
              offset * dateOffset
            )
          ).format('YYYY-MM-DD')

    // Ensure new start date is not before today
    const today = momentJalaali(getTodayDate()).startOf('day')
    const finalStartDate =
      (range === 'MONTH' && momentJalaali(newStartDate).isBefore(today)) ||
      (range !== 'MONTH' &&
        momentJalaali(newStartDate).isBefore(today.startOf('months')))
        ? today.format('YYYY-MM-DD')
        : newStartDate

    // Update calendarDateRange based on selectedRange
    const endDate =
      range === 'MONTH'
        ? addDaysToDate(finalStartDate, 31)
        : range === 'THREE_MONTH'
        ? momentJalaali(finalStartDate)
            .startOf('month')
            .add(90, 'days')
            .endOf('month')
            .format('YYYY-MM-DD')
        : momentJalaali(finalStartDate)
            .startOf('month')
            .add(365, 'days')
            .endOf('month')
            .format('YYYY-MM-DD') // Default to YEAR if not MONTH or THREE_MONTH

    setselectedDateInDatePicker({
      ...selectedDateInDatePicker,
      gregorianStartDate: finalStartDate,
      gregorianEndDate: endDate
    })
  }
  const isAtStartOrToday = useMemo(() => {
    const startDate = momentJalaali(selectedDateInDatePicker.gregorianStartDate)
    const today = momentJalaali(getTodayDate())

    // Disable if the selected date is today or the first day of the current month
    const isToday = startDate.isSame(today, 'day')
    const isFirstDayOfCurrentMonth =
      startDate.isSame(today, 'month') && startDate.date() === 1

    return isToday || isFirstDayOfCurrentMonth
  }, [selectedDateInDatePicker.gregorianStartDate])

  return (
    <Paper
      className={
        range === 'YEAR'
          ? classes.paperYearly
          : range === 'THREE_MONTH'
          ? classes.paperQuarterly
          : classes.paperMonthly
      }
      elevation={5}>
      <div className={classes.bigDateContainer}>
        <IconButton
          size="small"
          disabled={isAtStartOrToday}
          onClick={() => handleRangeChange(-1, false)}>
          <DoubleArrowIcon
            className={[classes.prevPageIcon, classes.arrowIcon].join(' ')}
            fontSize="small"
          />
        </IconButton>
        <IconButton
          size="small"
          disabled={isAtStartOrToday}
          onClick={() => handleRangeChange(-1, true)}>
          <LeftArrowIcon fontSize="small" className={classes.arrowIcon} />
        </IconButton>
        {datesList && datesList.length > 0 && (
          <div className={classes.bidDateContainer} ref={wrapperRef}>
            <ButtonBase onClick={() => setisOpen(!isOpen)}>
              <Typography
                variant="h3"
                color="primary"
                className={classes.bigDate}>
                {displayDate}
              </Typography>
            </ButtonBase>
            {isOpen && (
              <Fade in={isOpen}>
                <div className={classes.datePickerContainer}>
                  <DateRangePicker
                    // single Date---------
                    // onChange={(d: DateObject) => setselectedDateInDatePicker(d)}
                    // dateObject={selectedDateInDatePicker}
                    // range={false}
                    // default(range):---------
                    // onChange={(d: DateObject) => setselectedDateInDatePicker(d)}
                    // dateObject={selectedDateInDatePicker}
                    // auto select range:---------
                    onChange={(d: DateObject) => setselectedDateInDatePicker(d)}
                    dateObject={selectedDateInDatePicker}
                    autoSelectedEndDate
                    numberOfHoveredItems={perPage}
                    type={lang === 'fa' ? 'jalaali' : 'gregorian'}
                    onDoneClicked={() => setisOpen(false)}
                    // multiple select:--------------
                    // onChange={(d: DateObject) =>
                    //   setdateObjectList((currentList) => [...currentList, d])
                    // }
                    // multipleDates
                    // dateObjectList={dateObjectList}
                  />
                </div>
              </Fade>
            )}
          </div>
        )}
        <IconButton size="small" onClick={() => handleRangeChange(1, true)}>
          <RightArrowIcon fontSize="small" className={classes.arrowIcon} />
        </IconButton>
        <IconButton size="small" onClick={() => handleRangeChange(1, false)}>
          <DoubleArrowIcon fontSize="small" className={classes.arrowIcon} />
        </IconButton>
      </div>
      {datesList.map((item, index) => {
        const startDay = Math.floor(index % 3) * 10 + 1

        // Determine the end day; if it's the third segment, use the actual end of the month
        const endDay =
          index % 3 === 2
            ? moment(item.gregorianDate.date).endOf('month').date() // Last day of the month
            : startDay + 9 // 10-day range (e.g., 1-10, 11-20)

        return (
          <div
            className={[
              classes.dayContainer,
              isWeekend(item) && range === 'MONTH'
                ? classes.weekendDayContainer
                : '',
              range === 'MONTH' && lang === 'fa'
                ? classes.dayContainerSmallFa
                : classes.dayContainerSmallEn
            ].join(' ')}
            key={item.gregorianDate.date}>
            {range === 'YEAR' && (
              // Yearly View: Display full month name
              <span>
                {item[lang === 'fa' ? 'jalaaliDate' : 'gregorianDate'].month}
              </span>
            )}

            {range === 'THREE_MONTH' && (
              // Three-Month View: Display 10-day range within each month
              <>
                <span>
                  {
                    item[lang === 'fa' ? 'jalaaliDate' : 'gregorianDate']
                      .shortMonth
                  }{' '}
                  {startDay}-{endDay}
                </span>
              </>
            )}

            {range === 'MONTH' && (
              // Monthly View: Display day-specific information
              <>
                <span>
                  {
                    item[lang === 'fa' ? 'jalaaliDate' : 'gregorianDate']
                      .shortMonth
                  }
                </span>
                <Typography variant="h4">
                  {
                    item[lang === 'fa' ? 'jalaaliDate' : 'gregorianDate']
                      .monthDay
                  }
                </Typography>
                <span>{item.gregorianDate.weekDay}</span>
              </>
            )}
          </div>
        )
      })}
    </Paper>
  )
}

export default DateRow
